import { useCallback } from 'react';
import Button from 'devextreme-react/button';
import { fileExtension, type MediaKind } from './mediaFileTypes';
import {
  copyImageToClipboard,
  downloadMediaFile,
  shareMediaViewerLink
} from './mediaViewerActions';
import { formatMediaDateModified, formatMediaFileSize } from './mediaViewerFormatters';
import type { MediaFileMetadata } from './mediaViewerMetadata';
import './MediaViewerChrome.css';

export interface MediaViewerChromeBarProps {
  kind: MediaKind;
  mountId: string;
  relativePath: string;
  metadata: MediaFileMetadata | null;
  disabled?: boolean;
}

export function MediaViewerChromeBar({
  kind,
  mountId,
  relativePath,
  metadata,
  disabled = false
}: MediaViewerChromeBarProps) {
  const onCopy = useCallback(() => {
    void copyImageToClipboard(mountId, relativePath);
  }, [mountId, relativePath]);

  const onDownload = useCallback(() => {
    downloadMediaFile(mountId, relativePath);
  }, [mountId, relativePath]);

  const onShare = useCallback(() => {
    void shareMediaViewerLink(kind, mountId, relativePath);
  }, [kind, mountId, relativePath]);

  const modifiedLabel = formatMediaDateModified(metadata?.dateModified);
  const sizeLabel = formatMediaFileSize(metadata?.size);
  const isGif = fileExtension(relativePath) === 'gif';

  return (
    <div className="media-viewer-chrome__bar" role="toolbar" aria-label="Media viewer">
      <div className="media-viewer-chrome__meta" aria-label="File details">
        <span className="media-viewer-chrome__meta-item">
          <span className="media-viewer-chrome__meta-label">Modified</span>
          <span className="media-viewer-chrome__meta-value">{modifiedLabel}</span>
        </span>
        <span className="media-viewer-chrome__meta-sep" aria-hidden="true">
          ·
        </span>
        <span className="media-viewer-chrome__meta-item">
          <span className="media-viewer-chrome__meta-label">Size</span>
          <span className="media-viewer-chrome__meta-value">{sizeLabel}</span>
        </span>
      </div>
      <div className="media-viewer-chrome__actions">
        {kind === 'image' ? (
          <Button
            icon="copy"
            text="Copy"
            stylingMode="text"
            hint={
              isGif
                ? 'Copy GIF (may not work in all browsers; use Download for the full animation)'
                : 'Copy image to clipboard'
            }
            disabled={disabled}
            onClick={onCopy}
          />
        ) : null}
        <Button
          icon="download"
          text="Download"
          stylingMode="text"
          hint="Download file"
          disabled={disabled}
          onClick={onDownload}
        />
        <Button
          icon="share"
          text="Share"
          stylingMode="text"
          hint="Share link to full-page viewer"
          disabled={disabled}
          onClick={onShare}
        />
      </div>
    </div>
  );
}
